/** Training bonds: labels and plain-language wording (pure; HR and My View). */

/** "₱58,500.00" */
export const peso = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : `₱${Number(n).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

export const BOND_STATUS = {
  NOT_SIGNED: { label: "Not signed", tone: "gray" },
  ATTENDED_NOT_SIGNED: { label: "Attended, not signed", tone: "amber" },
  SIGNED: { label: "Signed", tone: "blue" },
  ACTIVE: { label: "Active", tone: "green" },
  SEPARATED: { label: "Left during bond", tone: "red" },
  COMPLETED: { label: "Completed", tone: "gray" },
  RELEASED: { label: "Released", tone: "gray" },
  VOID: { label: "Void", tone: "gray" },
};
export const bondStatus = (status) =>
  BOND_STATUS[status] || { label: status || "—", tone: "gray" };

export const PAYBACK_RULE = {
  REDUCING: "Reduces monthly",
  FULL: "Full amount",
};
export const paybackRuleLabel = (rule) => PAYBACK_RULE[rule] || rule || "—";

export const PAYBACK_RULE_HELP =
  "Example: ₱60,000 over 24 months with 12 left owes ₱30,000 when reducing, ₱60,000 when full.";

const monthsText = (n) => `${n} ${Number(n) === 1 ? "month" : "months"}`;

/** What leaving during the bond costs, in plain words for the employee. */
export const paybackInWords = (rule, cost, months) => {
  const m = Number(months);
  const full = peso(cost);
  if (rule === "FULL")
    return `If you leave before the ${monthsText(m)} are over, you owe the full ${full}, however long you have stayed.`;
  if (rule !== "REDUCING")
    return `If you leave before the ${monthsText(m)} are over, you may have to pay back up to ${full}.`;
  if (m < 2)
    return `If you leave before the month is over, you owe the full ${full}.`;
  const left = Math.ceil(m / 2);
  return `If you leave before the ${monthsText(m)} are over, you pay back part of it. It goes down with each full month you stay: with ${left} of ${m} months left, you would owe ${peso((Number(cost) * left) / m)}.`;
};

/** Actions HR can take on a bond, from its status. */
export const canAdjustBond = (status) =>
  ["NOT_SIGNED", "SIGNED", "ATTENDED_NOT_SIGNED"].includes(status);
export const canReleaseBond = (status) =>
  [
    "NOT_SIGNED",
    "SIGNED",
    "ATTENDED_NOT_SIGNED",
    "ACTIVE",
    "SEPARATED",
  ].includes(status);
export const canAttachSignedCopy = (status) =>
  status !== "VOID" && status !== "RELEASED";

/** "" when the file can be uploaded as a signed copy. */
export const signedCopyProblem = (file, maxBytes = 10 * 1024 * 1024) => {
  if (!file) return "Choose a file.";
  if (file.size > maxBytes) return "The file is larger than 10 MB.";
  const type = file.type || "";
  if (type === "application/pdf" || type.startsWith("image/")) return "";
  return "Upload a PDF or an image.";
};

/** Seminar form: problems with the bond fields; [] when off or complete. */
export const bondFormProblems = ({
  bondRequired,
  bondCost,
  bondMonths,
  bondTerms,
}) => {
  if (!bondRequired) return [];
  const list = [];
  if (!(Number(bondCost) > 0)) list.push("Enter the training cost.");
  const months = Number(bondMonths);
  if (!Number.isInteger(months) || months < 1 || months > 120)
    list.push("Bond months must be a whole number from 1 to 120.");
  if (!String(bondTerms || "").trim())
    list.push("Enter the agreement wording.");
  return list;
};

/** Seminar body for the bond; nothing at all once someone has accepted. */
export const bondPayload = (form, { locked = false } = {}) => {
  if (locked) return {};
  if (!form.bondRequired) return { bondRequired: false };
  return {
    bondRequired: true,
    bondCost: Number(form.bondCost),
    bondMonths: Number(form.bondMonths),
    bondTerms: String(form.bondTerms).trim(),
    bondPaybackRule: form.bondPaybackRule || null,
  };
};

/** People marked attended with no accepted agreement: no bond runs for them. */
export const attendedUnsigned = (participants = []) =>
  participants.filter(
    (p) => p.status === "ATTENDED" && !p.bondAcceptedAt && !p.bondReleasedAt,
  );

/** A self check-in away from the venue, or at a venue with no pin. */
export const isFlaggedCheckIn = (p) =>
  p.checkInMethod === "SELF" && p.locationVerified === false;
